import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Ban, Check, CornerUpLeft, Download, File, FileImage, FilePenLine, FileText,
  Forward, Inbox, Link2, Mail, MailOpen, MoreHorizontal, Paperclip, Plus, RefreshCw,
  ReplyAll, RotateCcw, Search, Send, Trash2, Unlink, UserRound, Wrench, X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API, apiError, apiFieldErrors } from "../lib/api";
import { useI18n } from "../lib/i18n";

const emptyComposer = { mode: "new", sourceId: null, to: "", cc: "", subject: "", body: "", files: [], context: null, replyContext: null };
const MAIL_FOLDERS = [
  ["inbox", Inbox], ["unread", MailOpen], ["sent", Send],
  ["drafts", FilePenLine], ["spam", Ban], ["trash", Trash2],
];
const EMPTY_FOLDER_KEYS = {
  inbox: "mailInboxEmpty", unread: "mailUnreadEmpty", sent: "mailSentEmpty",
  drafts: "mailDraftsEmpty", spam: "mailSpamEmpty", trash: "mailTrashEmpty",
};

export function mailOauthReturnPath(pathname = window.location.pathname) {
  if (pathname === "/admin/mail" || pathname === "/care/mail") return pathname;
  return "/care?view=email";
}

const timestamp = (message) => {
  const value = message?.date || message?.internalDate || message?.internal_date;
  if (typeof value === "number" || /^\d+$/.test(String(value || ""))) {
    const numeric = Number(value);
    return numeric > 100000000000 ? numeric : numeric * 1000;
  }
  const parsed = new Date(value || 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export function sortThreadMessages(rows = []) {
  return [...rows].sort((left, right) => timestamp(left) - timestamp(right)
    || String(left?.id || "").localeCompare(String(right?.id || "")));
}

function senderIdentity(value = "") {
  const match = String(value).trim().match(/^(.*?)\s*<([^<>]+)>$/);
  if (!match) return { name: String(value).trim(), address: "" };
  return { name: match[1].trim().replace(/^"|"$/g, "") || match[2].trim(), address: match[2].trim() };
}

function normalizedEmail(value = "") {
  const identity = senderIdentity(value);
  const candidate = identity.address || String(value).match(/[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || "";
  return candidate.trim().toLowerCase();
}

export function isOwnMailMessage(part, mailboxEmail) {
  const sender = normalizedEmail(part?.sender);
  const mailboxes = Array.isArray(mailboxEmail) ? mailboxEmail : [mailboxEmail];
  return Boolean(sender && mailboxes.some((value) => sender === normalizedEmail(value)));
}

function emailAddresses(value = "") {
  return String(Array.isArray(value) ? value.join(",") : value)
    .match(/[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi)
    ?.map((address) => address.toLowerCase()) || [];
}

export function latestExternalMessage(rows = [], mailboxEmails = []) {
  return [...sortThreadMessages(rows)].reverse().find((part) => !isOwnMailMessage(part, mailboxEmails)) || null;
}

export function replyAddressForMessage(part, mailboxEmails = []) {
  const owned = new Set(emailAddresses(mailboxEmails));
  const replyTo = emailAddresses(part?.reply_to).find((address) => !owned.has(address));
  if (replyTo) return replyTo;
  return emailAddresses(part?.sender).find((address) => !owned.has(address)) || "";
}

export function replyAllCcForMessage(part, mailboxEmails = []) {
  const owned = new Set(emailAddresses(mailboxEmails));
  const direct = replyAddressForMessage(part, mailboxEmails);
  return [...new Set([...emailAddresses(part?.to), ...emailAddresses(part?.cc)])]
    .filter((address) => !owned.has(address) && address !== direct).join(", ");
}

function messageDate(value, lang, compact = false) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "de" ? "de-DE" : "en-GB";
  const today = new Date();
  const sameDay = date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
  if (compact && sameDay) return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(date);
  if (compact) return new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "2-digit", year: date.getFullYear() === today.getFullYear() ? undefined : "2-digit",
  }).format(date);
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(date);
}

function serviceOptionLabel(service, player, lang) {
  const category = service.category_name || service.title || service.id;
  const title = service.title && service.title !== category ? service.title : "";
  const scheduled = service.details?.scheduled_start_at;
  const parts = [category, title].filter(Boolean);
  if (scheduled) {
    const date = new Date(scheduled);
    if (!Number.isNaN(date.getTime())) {
      const locale = lang === "de" ? "de-DE" : "en-GB";
      const options = { timeZone: player?.timezone || "UTC" };
      parts.push(new Intl.DateTimeFormat(locale, { ...options, day: "2-digit", month: "2-digit", year: "numeric" }).format(date));
      parts.push(new Intl.DateTimeFormat(locale, { ...options, hour: "2-digit", minute: "2-digit" }).format(date));
    }
  }
  return parts.join(" · ");
}

const attachmentMeta = (row, t) => {
  const mime = String(row.mime_type || "").toLowerCase();
  const Icon = mime === "application/pdf" ? FileText : mime.startsWith("image/") ? FileImage : File;
  const type = mime === "application/pdf" ? "PDF" : mime.startsWith("image/") ? t("mailAttachmentImage") : mime.includes("word") || mime.includes("document") ? t("mailAttachmentDocument") : t("mailAttachmentFile");
  const bytes = Number(row.size || 0);
  const size = bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
  return { Icon, type, size };
};

const normalizedAttachmentMime = (value) => String(value || "").split(";", 1)[0].trim().toLowerCase();
const PREVIEW_MIME_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/webp"]);

export function parseMoneyAmount(value) {
  let normalized = String(value ?? "").trim().replace(/\s/g, "");
  if (!normalized) return null;
  const comma = normalized.lastIndexOf(",");
  const dot = normalized.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) {
    normalized = comma > dot
      ? normalized.replace(/\./g, "").replace(",", ".")
      : normalized.replace(/,/g, "");
  } else if (comma >= 0) normalized = normalized.replace(",", ".");
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export const parseInvoiceAmount = parseMoneyAmount;

function formatDocumentMoney(value, currency, lang) {
  if (value == null) return "";
  return new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-GB", {
    style: "currency", currency: currency || "EUR",
  }).format(Number(value));
}

function StoredDocumentDetails({ documentImport }) {
  const { t, lang } = useI18n();
  if (documentImport?.status !== "completed") return null;
  if (documentImport.document_type !== "quote") return <em><Check size={12} />{t("mailStoredAs")} {t(documentTypeKey(documentImport.document_type))}</em>;
  return <span className="pc-mail-stored-offer"><em><Check size={12} />{t("mailOfferStored")}</em>{documentImport.provider_name && <small>{documentImport.provider_name}</small>}<small>{documentImport.offer_number || t("mailNoOfferNumber")}{documentImport.quoted_amount != null ? ` · ${formatDocumentMoney(documentImport.quoted_amount, documentImport.currency, lang)}` : ""}</small></span>;
}

export const attachmentDownloadUrl = (connectionId, messageId, row) => `${API}/care/mail/connections/${encodeURIComponent(connectionId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(row.id)}?filename=${encodeURIComponent(row.filename || "attachment")}&disposition=attachment`;

export function collectThreadAttachments(thread = []) {
  return [...thread].flatMap((part) => (part.attachments || []).map((attachment, attachmentIndex) => ({
    ...attachment,
    source_message_id: part.id,
    source_sender: part.sender,
    source_date: part.date,
    source_timestamp: timestamp(part),
    attachment_index: attachmentIndex,
  }))).sort((left, right) => right.source_timestamp - left.source_timestamp
    || String(left.source_message_id || "").localeCompare(String(right.source_message_id || ""))
    || left.attachment_index - right.attachment_index);
}

export function suggestedAttachmentDocumentTypes(part, mailboxEmail) {
  if (!part?.attachments?.length || isOwnMailMessage(part, mailboxEmail)) return [];
  const searchable = [
    part.subject, part.active_body, part.text, part.snippet,
    ...(part.attachments || []).map((row) => row.filename),
  ].filter(Boolean).join(" ").toLocaleLowerCase("de-DE");
  return [
    ["quote", "angebot"],
    ["booking_confirmation", "beauftragung"],
    ["invoice", "rechnung"],
  ].filter(([, keyword]) => searchable.includes(keyword)).map(([type]) => type);
}

function documentTypeKey(type) {
  if (type === "quote") return "mailDocumentQuote";
  if (type === "booking_confirmation") return "mailDocumentCommission";
  return "mailDocumentInvoice";
}

function AttachmentList({ connectionId, part, mailboxEmails, onOpen, onSuggest }) {
  const { t } = useI18n();
  const rows = part.attachments || [];
  const suggestions = suggestedAttachmentDocumentTypes(part, mailboxEmails);
  return rows.length ? <div className="pc-mail-attachments" data-testid="mail-attachments">
    {rows.map((row) => {
      const { Icon, type, size } = attachmentMeta(row, t);
      return <div key={row.id} className="pc-mail-attachment">
        <span className="pc-mail-attachment__icon"><Icon size={17} /></span>
        <span className="pc-mail-attachment__copy"><strong title={row.filename}>{row.filename}</strong><small>{type} · {size}</small><StoredDocumentDetails documentImport={row.document_import} /></span>
        <span className="pc-mail-attachment__actions"><button type="button" onClick={() => onOpen(part.id, row.id)}>{t("mailOpen")}</button><a target="_blank" rel="noreferrer" download href={attachmentDownloadUrl(connectionId, part.id, row)} aria-label={`${t("mailDownload")}: ${row.filename}`}><Download size={14} /></a></span>
      </div>;
    })}
    {suggestions.length > 0 && <div className="pc-mail-document-suggestions" data-testid={`mail-document-suggestions-${part.id}`}><small>{t("mailDocumentSuggestion")}</small>{suggestions.map((type) => <button type="button" key={type} onClick={() => onSuggest(part, type)}>{t(documentTypeKey(type))}</button>)}</div>}
  </div> : null;
}

function MessageBody({ part }) {
  if (part.active_html) return <div className="pc-mail-html" dangerouslySetInnerHTML={{ __html: part.active_html }} />;
  if (part.active_body !== undefined && part.active_body !== null) {
    return <pre>{part.active_body || part.text || part.snippet || ""}</pre>;
  }
  if (part.html) return <div className="pc-mail-html" dangerouslySetInnerHTML={{ __html: part.html }} />;
  const text = part.text || part.snippet || "";
  const lines = text.split("\n");
  const quoteAt = lines.findIndex((line) => /^\s*(>|On .+wrote:|Am .+schrieb.+:|-----Original Message-----)/i.test(line));
  if (quoteAt < 0) return <pre>{text}</pre>;
  return <><pre>{lines.slice(0, quoteAt).join("\n")}</pre><div className="pc-mail-quote">{lines.slice(quoteAt).join("\n")}</div></>;
}

function AttachmentWorkspace({
  connection, rows, selectedKey, onSelect, onBack, onImport,
  focusedDocumentType, setFocusedDocumentType, linkedService, linkedProvider, linkedProviderId,
  busy, error,
  fieldErrors, onFieldErrors,
}) {
  const { t, lang } = useI18n();
  const selected = rows.find((row) => `${row.source_message_id}:${row.id}` === selectedKey) || null;
  const [preview, setPreview] = useState({ state: "idle", url: "" });
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [invoice, setInvoice] = useState({ invoice_number: "", amount: "", currency: "EUR" });
  const [offer, setOffer] = useState({ offer_number: "", quoted_amount: "", currency: "EUR", valid_until: "" });
  const [booking, setBooking] = useState({ related_offer_document_id: "" });
  const [confirmation, setConfirmation] = useState(null);
  const selectedMime = normalizedAttachmentMime(selected?.mime_type);
  const selectedFilename = selected?.filename;
  const selectedId = selected?.id;
  const selectedSourceMessageId = selected?.source_message_id;
  const selectedIsPdf = selectedMime === "application/pdf";
  const selectedIsImage = selectedMime.startsWith("image/");

  useEffect(() => {
    if (focusedDocumentType !== "invoice") return;
    setInvoice((current) => ({
      ...current,
      amount: current.amount || linkedService?.external_price || "",
      currency: linkedService?.currency || current.currency || "EUR",
    }));
  }, [focusedDocumentType, linkedService?.currency, linkedService?.external_price]);

  useEffect(() => {
    setOffer((current) => ({ ...current, currency: linkedService?.currency || current.currency || "EUR" }));
  }, [linkedService?.currency]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    if (!selectedId || !PREVIEW_MIME_TYPES.has(selectedMime)) {
      setPreview({ state: "unsupported", url: "" });
      return () => {};
    }
    setPreview({ state: "loading", url: "" });
    api.get(`/care/mail/connections/${connection.id}/messages/${selectedSourceMessageId}/attachments/${selectedId}`, {
      params: { filename: selectedFilename, disposition: "inline" }, responseType: "blob",
    }).then(({ data, headers }) => {
      if (!active) return;
      const responseMime = normalizedAttachmentMime(headers?.["content-type"] || data?.type);
      const previewMime = PREVIEW_MIME_TYPES.has(responseMime) ? responseMime : selectedMime;
      if (!PREVIEW_MIME_TYPES.has(previewMime)) throw new Error("Unsupported attachment preview type");
      const blob = new Blob([data], { type: previewMime });
      objectUrl = URL.createObjectURL(blob);
      setPreview({ state: "ready", url: objectUrl });
    }).catch(() => { if (active) setPreview({ state: "error", url: "" }); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [connection.id, previewAttempt, selectedFilename, selectedId, selectedMime, selectedSourceMessageId]);

  const importStatus = selected?.document_import?.status === "completed" ? selected.document_import : null;
  const submitDocumentType = (type) => {
    setFocusedDocumentType(type);
    onFieldErrors({});
    setConfirmation(null);
  };
  const submitInvoice = (event) => {
    event.preventDefault();
    const invoiceNumber = invoice.invoice_number.trim();
    const amount = parseMoneyAmount(invoice.amount);
    const validation = {};
    if (!invoiceNumber) validation.invoice_number = t("mailInvoiceNumberRequired");
    if (amount === null) validation.amount = t("mailInvoiceAmountInvalid");
    onFieldErrors(validation);
    if (Object.keys(validation).length) return;
    onImport(selected, "invoice", {
      invoice_number: invoiceNumber,
      amount,
      currency: invoice.currency,
    });
  };
  const submitOffer = (event) => {
    event.preventDefault();
    const quotedAmount = parseMoneyAmount(offer.quoted_amount);
    const validation = {};
    if (quotedAmount === null) validation.quoted_amount = t("mailOfferAmountInvalid");
    onFieldErrors(validation);
    if (Object.keys(validation).length) return;
    setConfirmation({
      documentType: "quote",
      metadata: {
        offer_number: offer.offer_number.trim() || null,
        quoted_amount: quotedAmount,
        currency: offer.currency,
        valid_until: offer.valid_until || null,
      },
    });
  };
  const submitBooking = (event) => {
    event.preventDefault();
    setConfirmation({
      documentType: "booking_confirmation",
      metadata: { related_offer_document_id: booking.related_offer_document_id || null },
    });
  };
  const confirmFiling = async () => {
    if (!confirmation) return;
    const succeeded = await onImport(selected, confirmation.documentType, confirmation.metadata);
    if (succeeded) setConfirmation(null);
  };
  const relatedOffers = (linkedService?.quote_documents || []).filter((document) => (
    (document.provider_id || null) === (linkedProviderId || null)
  ));
  const providerName = linkedProvider?.name || t("mailNoLinkedProvider");
  const serviceLabel = [linkedService?.category_name, linkedService?.title]
    .filter((value, index, values) => value && values.indexOf(value) === index).join(" · ") || "–";
  const serviceDate = linkedService?.scheduled_start_at
    ? messageDate(linkedService.scheduled_start_at, lang) : "";

  return <div className="pc-mail-attachment-workspace" data-testid="mail-attachment-workspace">
    <header className="pc-mail-attachment-workspace__header"><button type="button" onClick={onBack}><ArrowLeft size={15} />{t("mailBackToEmail")}</button><div><small>{t("mailAttachmentWorkspace")}</small><strong>{t("mailAllAttachments")} · {rows.length}</strong></div></header>
    <div className="pc-mail-attachment-workspace__body">
      <aside className="pc-mail-attachment-browser" aria-label={t("mailAllAttachments")}>
        {rows.map((row) => {
          const key = `${row.source_message_id}:${row.id}`;
          const { Icon, type, size } = attachmentMeta(row, t);
          const sender = senderIdentity(row.source_sender || t("mailUnknownSender"));
          return <button type="button" key={key} className="pc-mail-attachment-browser__row" aria-current={selectedKey === key ? "true" : undefined} onClick={() => onSelect(key)} data-testid={`mail-workspace-attachment-${row.id}`}>
            <span className="pc-mail-attachment__icon"><Icon size={18} /></span>
            <span><strong>{row.filename}</strong><small>{type} · {size}</small><small>{sender.name || sender.address} · {messageDate(row.source_date, lang)}</small><StoredDocumentDetails documentImport={row.document_import} /></span>
          </button>;
        })}
      </aside>
      <main className="pc-mail-attachment-preview">
        {!selected && <div className="pc-mail-attachment-preview__empty"><Paperclip size={26} /><p>{t("mailChooseAttachment")}</p></div>}
        {selected && <>
          <div className="pc-mail-attachment-preview__title"><div><strong>{selected.filename}</strong><small>{attachmentMeta(selected, t).type} · {attachmentMeta(selected, t).size}</small></div><a href={attachmentDownloadUrl(connection.id, selected.source_message_id, selected)} target="_blank" rel="noreferrer" download><Download size={15} />{t("mailDownload")}</a></div>
          <div className="pc-mail-attachment-preview__frame" data-testid="mail-attachment-preview">
            {preview.state === "loading" && <div className="pc-mail-attachment-preview__empty"><div className="pc-spinner" /><p>{t("mailPreviewLoading")}</p></div>}
            {preview.state === "ready" && selectedIsPdf && <iframe title={selected.filename} src={preview.url} referrerPolicy="no-referrer" />}
            {preview.state === "ready" && selectedIsImage && <img src={preview.url} alt={selected.filename} />}
            {["unsupported", "error"].includes(preview.state) && <div className="pc-mail-attachment-preview__empty"><File size={30} /><strong>{selected.filename}</strong><p>{preview.state === "error" && selectedIsPdf ? t("mailPdfPreviewFailed") : t("mailPreviewUnavailable")}</p><div className="pc-mail-attachment-preview__empty-actions">{preview.state === "error" && <button type="button" onClick={() => setPreviewAttempt((value) => value + 1)}><RefreshCw size={15} />{t("retry")}</button>}<a href={attachmentDownloadUrl(connection.id, selected.source_message_id, selected)} target="_blank" rel="noreferrer" download><Download size={15} />{t("mailDownload")}</a></div></div>}
          </div>
        </>}
        <section className="pc-mail-document-actions" data-testid="mail-document-actions">
          <div><small>{t("mailDocumentActions")}</small><strong>{!selected ? t("mailChooseAttachment") : importStatus ? `${t("mailStoredAs")} ${t(documentTypeKey(importStatus.document_type))}` : t("mailStoreInService")}</strong></div>
          <div className="pc-mail-document-actions__buttons">
            {["quote", "booking_confirmation", "invoice"].map((type) => <button type="button" key={type} disabled={!selected || busy || Boolean(importStatus)} data-active={focusedDocumentType === type ? "true" : "false"} onClick={() => submitDocumentType(type)}>{t(documentTypeKey(type))}</button>)}
          </div>
          {selected && focusedDocumentType === "quote" && !importStatus && <form className="pc-mail-document-fields pc-mail-offer-fields" onSubmit={submitOffer} noValidate><p className="pc-mail-document-provider"><span>{t("provider")}</span><strong>{providerName}</strong></p><label><span>{t("mailOfferNumber")}</span><input className="pc-input" value={offer.offer_number} onChange={(event) => setOffer({ ...offer, offer_number: event.target.value })} placeholder={t("mailOfferNumberOptional")} /></label><label><span>{t("mailQuotedAmount")}</span><input className="pc-input" type="text" inputMode="decimal" aria-invalid={fieldErrors.quoted_amount ? "true" : undefined} value={offer.quoted_amount} onChange={(event) => { setOffer({ ...offer, quoted_amount: event.target.value }); onFieldErrors({ ...fieldErrors, quoted_amount: "" }); }} />{fieldErrors.quoted_amount && <small className="pc-mail-field-error">{fieldErrors.quoted_amount}</small>}</label><label><span>{t("currency")}</span><select className="pc-input" value={offer.currency} onChange={(event) => setOffer({ ...offer, currency: event.target.value })}><option>EUR</option><option>GBP</option><option>USD</option></select></label><label><span>{t("mailValidUntil")}</span><input className="pc-input" type="date" value={offer.valid_until} onChange={(event) => setOffer({ ...offer, valid_until: event.target.value })} /></label><button className="pc-btn pc-btn-primary" disabled={busy} type="submit">{t("mailStoreOffer")}</button></form>}
          {selected && focusedDocumentType === "booking_confirmation" && !importStatus && <form className="pc-mail-document-fields pc-mail-booking-fields" onSubmit={submitBooking}><p className="pc-mail-document-provider"><span>{t("provider")}</span><strong>{providerName}</strong></p>{relatedOffers.length > 0 && <label><span>{t("mailRelatedOffer")}</span><select className="pc-input" aria-invalid={fieldErrors.related_offer_document_id ? "true" : undefined} value={booking.related_offer_document_id} onChange={(event) => { setBooking({ related_offer_document_id: event.target.value }); onFieldErrors({ ...fieldErrors, related_offer_document_id: "" }); }}><option value="">{t("mailNoOfferRelation")}</option>{relatedOffers.map((document) => <option key={document.id} value={document.id}>{document.offer_number || t("mailNoOfferNumber")}{document.quoted_amount != null ? ` · ${formatDocumentMoney(document.quoted_amount, document.currency, lang)}` : ""}</option>)}</select>{fieldErrors.related_offer_document_id && <small className="pc-mail-field-error">{fieldErrors.related_offer_document_id}</small>}</label>}<button className="pc-btn pc-btn-primary" disabled={busy} type="submit">{t("mailStoreCommission")}</button></form>}
          {selected && focusedDocumentType === "invoice" && !importStatus && <form className="pc-mail-invoice-fields" onSubmit={submitInvoice} noValidate><label><span>{t("invoiceNumber")}</span><input className="pc-input" aria-invalid={fieldErrors.invoice_number ? "true" : undefined} value={invoice.invoice_number} onChange={(event) => { setInvoice({ ...invoice, invoice_number: event.target.value }); onFieldErrors({ ...fieldErrors, invoice_number: "" }); }} />{fieldErrors.invoice_number && <small className="pc-mail-field-error">{fieldErrors.invoice_number}</small>}</label><label><span>{t("amount")}</span><input className="pc-input" type="text" inputMode="decimal" aria-invalid={fieldErrors.amount ? "true" : undefined} value={invoice.amount} onChange={(event) => { setInvoice({ ...invoice, amount: event.target.value }); onFieldErrors({ ...fieldErrors, amount: "" }); }} />{fieldErrors.amount && <small className="pc-mail-field-error">{fieldErrors.amount}</small>}</label><label><span>{t("currency")}</span><select className="pc-input" aria-invalid={fieldErrors.currency ? "true" : undefined} value={invoice.currency} onChange={(event) => { setInvoice({ ...invoice, currency: event.target.value }); onFieldErrors({ ...fieldErrors, currency: "" }); }}><option>EUR</option><option>GBP</option><option>USD</option></select>{fieldErrors.currency && <small className="pc-mail-field-error">{fieldErrors.currency}</small>}</label><button className="pc-btn pc-btn-primary" disabled={busy} type="submit">{busy ? t("loading") : t("mailStoreInvoice")}</button></form>}
          {error && <p className="pc-mail-error" role="alert">{error}</p>}
        </section>
      </main>
    </div>
    {confirmation && <div className="pc-mail-filing-dialog-backdrop"><section className="pc-mail-filing-dialog" role="dialog" aria-modal="true" aria-labelledby="mail-filing-dialog-title"><h3 id="mail-filing-dialog-title">{t(confirmation.documentType === "quote" ? "mailConfirmOfferTitle" : "mailConfirmCommissionTitle")}</h3><dl><div><dt>{t("provider")}</dt><dd>{providerName}</dd></div><div><dt>{t("service")}</dt><dd>{serviceLabel}{serviceDate ? <small>{serviceDate}</small> : null}</dd></div>{confirmation.documentType === "quote" && <><div><dt>{t("mailOfferNumber")}</dt><dd>{confirmation.metadata.offer_number || t("mailNoOfferNumber")}</dd></div><div><dt>{t("mailQuotedAmount")}</dt><dd>{formatDocumentMoney(confirmation.metadata.quoted_amount, confirmation.metadata.currency, lang)}</dd></div></>}</dl><footer><button type="button" className="pc-btn pc-btn-ghost" disabled={busy} onClick={() => setConfirmation(null)}>{t("cancel")}</button><button type="button" className="pc-btn pc-btn-primary" disabled={busy} onClick={confirmFiling}>{busy ? t("loading") : t(confirmation.documentType === "quote" ? "mailStoreOffer" : "mailStoreCommission")}</button></footer></section></div>}
  </div>;
}

function Composer({ connection, draft, onChange, onClose, onSent }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sendingRef = useRef(false);
  const idempotencyKeyRef = useRef(null);
  if (!idempotencyKeyRef.current) {
    idempotencyKeyRef.current = globalThis.crypto?.randomUUID?.() || `${Date.now()}`;
  }
  const submit = async (event) => {
    event.preventDefault();
    if (sendingRef.current) return;
    sendingRef.current = true;
    setBusy(true); setError("");
    const form = new FormData();
    form.set("body", draft.body); if (draft.cc) form.set("cc", draft.cc);
    draft.files.forEach((file) => form.append("files", file));
    let path;
    if (draft.mode === "reply" || draft.mode === "reply_all") {
      form.set("reply_all", String(draft.mode === "reply_all"));
      path = `/care/mail/connections/${connection.id}/messages/${draft.sourceId}/reply`;
    } else if (draft.mode === "forward") {
      form.set("to", draft.to); path = `/care/mail/connections/${connection.id}/messages/${draft.sourceId}/forward`;
    } else {
      form.set("to", draft.to); form.set("subject", draft.subject);
      form.set("idempotency_key", idempotencyKeyRef.current);
      if (draft.context?.communication_type === "quote_request") {
        form.set("communication_type", "quote_request");
        form.set("service_id", draft.context.service_id);
        form.set("provider_id", draft.context.provider_id);
      }
      path = `/care/mail/connections/${connection.id}/send`;
    }
    try { const { data } = await api.post(path, form); await onSent(draft, data); }
    catch (requestError) { setError(apiError(requestError.response?.data?.detail)); }
    finally { sendingRef.current = false; setBusy(false); }
  };
  const title = draft.mode === "reply_all" ? t("mailReplyAll") : draft.mode === "reply" ? t("mailReply") : draft.mode === "forward" ? t("mailForward") : t("mailNew");
  const needsRecipient = !draft.mode.startsWith("reply");
  return <form className="pc-mail-composer" onSubmit={submit} data-testid="mail-composer">
      <header><div><p>{t("email")}</p><h2>{title}</h2></div><button type="button" className="pc-mail-icon-button" aria-label={t("cancel")} onClick={onClose} data-testid="mail-composer-close"><X size={18} /></button></header>
      <div className="pc-mail-composer__fields">
        <label><span>{t("mailTo")}</span><input value={draft.to} readOnly={!needsRecipient} required={needsRecipient} onChange={(e) => onChange({ ...draft, to: e.target.value })} data-testid="mail-to" /></label>
        <label><span>{t("mailCcShort")}</span><input value={draft.cc} onChange={(e) => onChange({ ...draft, cc: e.target.value })} data-testid="mail-cc" /></label>
        <label><span>{t("mailSubject")}</span><input value={draft.subject} readOnly={draft.mode !== "new"} required={draft.mode === "new"} onChange={(e) => onChange({ ...draft, subject: e.target.value })} data-testid="mail-subject" /></label>
      </div>
      {draft.replyContext && <div className="pc-mail-composer__reply-context" data-testid="mail-reply-context"><CornerUpLeft size={14} /><span>{t("mailReplyContext")}</span><strong>{draft.replyContext.name} · {draft.replyContext.date}</strong></div>}
      {draft.context?.communication_type === "quote_request" && <div className="pc-mail-composer__context" data-testid="mail-quote-context"><Link2 size={15} /><div><small>{t("quoteRequestContext")}</small><strong>{draft.context.player_name} · {draft.context.category_name || draft.context.service_title}</strong><span>{draft.context.provider_name}</span></div></div>}
      <textarea autoFocus className="pc-mail-composer__body" required value={draft.body} onChange={(e) => onChange({ ...draft, body: e.target.value })} placeholder={t("mailMessagePlaceholder")} data-testid="mail-body" />
      {draft.files.length > 0 && <div className="pc-mail-composer__files">{draft.files.map((file, index) => <span key={`${file.name}-${index}`}><Paperclip size={13} /><b>{file.name}</b><button type="button" aria-label={t("mailRemoveAttachment")} onClick={() => onChange({ ...draft, files: draft.files.filter((_, itemIndex) => itemIndex !== index) })}><X size={13} /></button></span>)}</div>}
      {error && <p className="pc-mail-error" role="alert">{error}</p>}
      <footer><label className="pc-mail-file" data-testid="mail-add-attachment"><Paperclip size={16} /><span>{t("mailAddAttachment")}</span><input type="file" multiple onChange={(e) => onChange({ ...draft, files: [...draft.files, ...e.target.files] })} /></label><div className="pc-mail-composer__actions"><button type="button" className="pc-btn pc-btn-ghost" onClick={onClose} data-testid="mail-composer-cancel">{t("cancel")}</button><button type="submit" className="pc-btn pc-btn-primary" data-testid="mail-send" disabled={busy || !draft.body.trim() || (needsRecipient && !draft.to.trim())}><Send size={15} />{busy ? t("mailSending") : t("send")}</button></div></footer>
  </form>;
}

export default function CareEmailWorkspace({ players = [] }) {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeMessage = searchParams.get("message") || "";
  const routeFocusMessage = searchParams.get("focus_message") || "";
  const routeConnection = searchParams.get("connection") || "";
  const quoteServiceId = searchParams.get("quote_service") || "";
  const quoteProviderId = searchParams.get("quote_provider") || "";
  const quoteLocale = searchParams.get("lang") || lang;
  const staffDocumentId = searchParams.get("staff_document") || "";
  const staffDocumentName = searchParams.get("staff_document_name") || "";
  const staffManagerName = searchParams.get("staff_manager_name") || "";
  const staffPeriod = searchParams.get("staff_period") || "";
  const staffEvidence = searchParams.get("staff_evidence") || "";
  const preserveUnreadRoute = searchParams.get("keep_unread") === "1";
  const [oauthOutcome] = useState(() => searchParams.get("oauth") || "");
  const [status, setStatus] = useState(null);
  const [connectionId, setConnectionId] = useState(routeConnection);
  const [folder, setFolder] = useState("inbox");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(routeMessage);
  const [detail, setDetail] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [pageStack, setPageStack] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [composer, setComposer] = useState(null);
  const [linkPlayer, setLinkPlayer] = useState("");
  const [linkService, setLinkService] = useState("");
  const [services, setServices] = useState([]);
  const [trashingMessageId, setTrashingMessageId] = useState("");
  const [restoringMessageId, setRestoringMessageId] = useState("");
  const [highlightedMessageId, setHighlightedMessageId] = useState("");
  const [selectedThreadMessageId, setSelectedThreadMessageId] = useState("");
  const [attachmentWorkspaceOpen, setAttachmentWorkspaceOpen] = useState(false);
  const [selectedAttachmentKey, setSelectedAttachmentKey] = useState("");
  const [attachmentScopeMessageId, setAttachmentScopeMessageId] = useState("");
  const [focusedDocumentType, setFocusedDocumentType] = useState("");
  const [documentBusy, setDocumentBusy] = useState(false);
  const documentImportingRef = useRef(false);
  const [documentError, setDocumentError] = useState("");
  const [documentFieldErrors, setDocumentFieldErrors] = useState({});
  const [assignmentAttention, setAssignmentAttention] = useState(false);
  const [oauthStarting, setOauthStarting] = useState("");
  const manualUnreadIds = useRef(new Set());
  const threadMessageRefs = useRef(new Map());
  const detailScrollRef = useRef(null);
  const threadScrollPositionRef = useRef(0);
  const assignmentPanelRef = useRef(null);
  const serviceSelectRef = useRef(null);
  const composerSlotRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const scrollToLatestAfterSendRef = useRef(false);
  const quoteIntentRef = useRef("");
  const staffForwardIntentRef = useRef("");
  const selectedMessageIdRef = useRef(routeMessage);
  const activeDetailRequestRef = useRef("");
  const queuedDetailRequestRef = useRef(null);

  const connection = useMemo(() => status?.connections?.find((row) => row.id === connectionId) || status?.connections?.[0], [status, connectionId]);
  const activeConnectionId = connection?.id || "";
  const activeConnectionStatus = connection?.status || "";
  const thread = useMemo(() => sortThreadMessages(detail?.thread?.length ? detail.thread : detail ? [detail] : []), [detail]);
  const ownedMailboxEmails = useMemo(() => (status?.connections || []).map((row) => row.mailbox_email), [status]);
  const latestExternal = useMemo(() => latestExternalMessage(thread, ownedMailboxEmails), [ownedMailboxEmails, thread]);
  const selectedThreadMessage = useMemo(() => thread.find((part) => part.id === selectedThreadMessageId) || null, [selectedThreadMessageId, thread]);
  const selectedReplyAddress = useMemo(
    () => replyAddressForMessage(selectedThreadMessage, ownedMailboxEmails),
    [ownedMailboxEmails, selectedThreadMessage],
  );
  const threadAttachments = useMemo(() => collectThreadAttachments(thread), [thread]);
  const visibleWorkspaceAttachments = useMemo(() => attachmentScopeMessageId
    ? threadAttachments.filter((row) => row.source_message_id === attachmentScopeMessageId)
    : threadAttachments, [attachmentScopeMessageId, threadAttachments]);
  const newestThreadMessageId = thread.at(-1)?.id || "";
  const unreadCount = messages.filter((row) => row.unread).length;
  useEffect(() => { selectedMessageIdRef.current = selectedId; }, [selectedId]);
  const loadStatus = useCallback(async () => {
    setState("loading"); setError("");
    try {
      const { data } = await api.get("/care/mail/status"); setStatus(data);
      if ((!connectionId || !data.connections?.some((row) => row.id === connectionId)) && data.connections?.[0]) setConnectionId(data.connections[0].id);
      setState("ready");
    } catch (requestError) { setError(apiError(requestError.response?.data?.detail)); setState("error"); }
  }, [connectionId]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (!oauthOutcome) return;
    const next = new URLSearchParams(searchParams);
    next.delete("oauth");
    setSearchParams(next, { replace: true });
    // The callback result is intentionally consumed exactly once after returning from Google.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!connection || !quoteServiceId || !quoteProviderId) return;
    const intentKey = `${quoteServiceId}:${quoteProviderId}:${quoteLocale}`;
    if (quoteIntentRef.current === intentKey) return;
    quoteIntentRef.current = intentKey;
    setDetail(null); setSelectedId(""); setError("");
    api.get(`/care/services/${quoteServiceId}/quote-request-draft`, { params: {
      provider_id: quoteProviderId, locale: quoteLocale,
    } }).then(({ data }) => {
      setComposer({ ...emptyComposer, ...data.draft, context: data.context, files: [] });
    }).catch((requestError) => {
      quoteIntentRef.current = "";
      setError(apiError(requestError.response?.data?.detail));
    });
  }, [connection, quoteLocale, quoteProviderId, quoteServiceId]);
  useEffect(() => {
    if (!connection || !staffDocumentId) return;
    if (staffForwardIntentRef.current === staffDocumentId) return;
    staffForwardIntentRef.current = staffDocumentId;
    setDetail(null); setSelectedId(""); setError("");
    const subject = staffEvidence === "eau"
      ? `${t("staffForwardEauSubject")} – ${staffManagerName || "–"}`
      : `${t("staffForwardSubject")} – ${staffManagerName || "–"} · ${staffPeriod || "–"}`;
    const body = t(staffEvidence === "eau" ? "staffForwardEauBody" : "staffForwardBody")
      .replace("{name}", staffManagerName || "–")
      .replace("{period}", staffPeriod || "–");
    const openComposer = (files = []) => setComposer({
      ...emptyComposer, subject, body, files,
      context: { communication_type: "staff_document_forwarding", staff_document_id: staffDocumentId },
    });
    const clearIntent = () => {
      const next = new URLSearchParams(searchParams);
      ["staff_document", "staff_document_name", "staff_manager_name", "staff_period", "staff_evidence"].forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    };
    if (staffEvidence === "eau") {
      openComposer(); clearIntent(); return;
    }
    api.get(`/admin/staff-documents/${staffDocumentId}/download`, { responseType: "blob" })
      .then(({ data }) => {
        const blob = data instanceof Blob ? data : new Blob([data]);
        const filename = staffDocumentName || "personal-document";
        const attachment = typeof File === "function"
          ? new File([blob], filename, { type: blob.type || "application/octet-stream" })
          : Object.assign(blob, { name: filename, lastModified: Date.now() });
        openComposer([attachment]);
      })
      .catch(() => { staffForwardIntentRef.current = ""; setError(t("staffForwardAttachmentError")); })
      .finally(clearIntent);
  }, [connection, searchParams, setSearchParams, staffDocumentId, staffDocumentName, staffEvidence, staffManagerName, staffPeriod, t]);
  useEffect(() => {
    let active = true;
    if (!linkPlayer) { setServices([]); return () => { active = false; }; }
    setServices([]);
    api.get("/care/services", { params: { player_id: linkPlayer } })
      .then(({ data }) => { if (active) setServices(data.services || []); })
      .catch((requestError) => { if (active) { setServices([]); setError(apiError(requestError.response?.data?.detail)); } });
    return () => { active = false; };
  }, [linkPlayer]);

  const loadMessages = useCallback(async (token = null) => {
    if (!activeConnectionId) return;
    if (!["connected", "temporary_error"].includes(activeConnectionStatus)) {
      const statusMessage = activeConnectionStatus === "reconnect_required" ? t("mailReconnectRequired")
        : activeConnectionStatus === "disconnected" ? t("mailDisconnectedStatus") : t("mailConnectionError");
      setMessages([]); setNextPage(null); setError(statusMessage); setState("error");
      return;
    }
    setState("loading"); setError("");
    try {
      const { data } = await api.get("/care/mail/messages", { params: {
        connection_id: activeConnectionId, folder, q: appliedQuery || undefined,
        page_token: token || undefined, page_size: 30,
      } });
      const nextMessages = (data.messages || []).map((row) => manualUnreadIds.current.has(row.id)
        ? { ...row, unread: true } : row);
      if (data.connection) {
        setStatus((current) => current ? {
          ...current,
          connections: (current.connections || []).map((row) => (
            row.id === data.connection.id ? data.connection : row
          )),
        } : current);
      }
      setMessages(nextMessages); setNextPage(data.next_page_token || null);
      setSelectedId((current) => routeMessage || (data.messages?.some((row) => row.id === current) ? current : ""));
      setState("ready");
    } catch (requestError) {
      const detailError = requestError.response?.data?.detail;
      if (detailError?.reconnect_required || detailError?.retryable) {
        const nextStatus = detailError.reconnect_required ? "reconnect_required" : "temporary_error";
        setStatus((current) => {
          if (!current || current.connections?.find((row) => row.id === activeConnectionId)?.status === nextStatus) return current;
          return {
            ...current,
            connections: (current.connections || []).map((row) => (
              row.id === activeConnectionId ? { ...row, status: nextStatus } : row
            )),
          };
        });
      }
      setError(detailError?.reconnect_required ? t("mailReconnectRequired") : t("mailLoadError"));
      setState("error");
    }
  }, [activeConnectionId, activeConnectionStatus, folder, appliedQuery, routeMessage, t]);
  useEffect(() => { if (activeConnectionId) { setPageStack([]); setCurrentPage(null); loadMessages(); } }, [activeConnectionId, folder, appliedQuery, loadMessages]);

  const openMessage = useCallback(async (id, syncRoute = true) => {
    if (!id || !activeConnectionId) return;
    const preserveUnread = !syncRoute && preserveUnreadRoute && routeMessage === id;
    const requestedDetail = {
      id, connectionId: activeConnectionId, preserveUnread,
      focusMessage: routeFocusMessage, ownedEmails: ownedMailboxEmails,
    };
    selectedMessageIdRef.current = id;
    setAttachmentWorkspaceOpen(false); setAttachmentScopeMessageId(""); setSelectedAttachmentKey("");
    setFocusedDocumentType(""); setDocumentError(""); setAssignmentAttention(false);
    setSelectedId(id); setDetail(null); setLinkPlayer(""); setLinkService(""); setError("");
    if (syncRoute) {
      manualUnreadIds.current.delete(id);
      const next = new URLSearchParams(searchParams);
      next.set("view", "email"); next.set("connection", activeConnectionId); next.set("message", id);
      next.delete("focus_message"); next.delete("keep_unread");
      setSearchParams(next, { replace: true });
    }
    if (activeDetailRequestRef.current) {
      if (activeDetailRequestRef.current === id) queuedDetailRequestRef.current = null;
      else queuedDetailRequestRef.current = requestedDetail;
      return;
    }
    let nextDetail = requestedDetail;
    while (nextDetail) {
      const loadingDetail = nextDetail;
      activeDetailRequestRef.current = loadingDetail.id;
      try {
        const { data } = await api.get(`/care/mail/messages/${loadingDetail.id}`, { params: {
          connection_id: loadingDetail.connectionId, mark_read: false,
        } });
        if (selectedMessageIdRef.current === loadingDetail.id) {
          const persistedLink = data.message.link || data.message.links?.[0] || null;
          const message = loadingDetail.preserveUnread ? { ...data.message, unread: true } : data.message;
          if (loadingDetail.preserveUnread) manualUnreadIds.current.add(loadingDetail.id);
          const loadedThread = sortThreadMessages(message?.thread?.length ? message.thread : [message]);
          const focused = loadedThread.find((part) => part.id === loadingDetail.focusMessage && !isOwnMailMessage(part, loadingDetail.ownedEmails));
          setSelectedThreadMessageId((focused || latestExternalMessage(loadedThread, loadingDetail.ownedEmails))?.id || "");
          setDetail(message); setLinkPlayer(persistedLink?.player_id || ""); setLinkService(persistedLink?.service_id || "");
          setMessages((rows) => rows.map((row) => row.id === loadingDetail.id ? {
            ...row, unread: Boolean(message.unread), link: persistedLink || row.link,
          } : row));
        }
      } catch (requestError) {
        if (selectedMessageIdRef.current === loadingDetail.id) setError(apiError(requestError.response?.data?.detail));
      }
      activeDetailRequestRef.current = "";
      const queuedDetail = queuedDetailRequestRef.current;
      queuedDetailRequestRef.current = null;
      nextDetail = queuedDetail && queuedDetail.id !== loadingDetail.id ? queuedDetail : null;
    }
  }, [activeConnectionId, ownedMailboxEmails, preserveUnreadRoute, routeFocusMessage, routeMessage, searchParams, setSearchParams]);
  useEffect(() => { if (routeMessage && activeConnectionId && routeMessage !== detail?.id) openMessage(routeMessage, false); }, [routeMessage, activeConnectionId, detail?.id, openMessage]);
  useEffect(() => {
    if (!detail || !thread.length) return undefined;
    const targetId = scrollToLatestAfterSendRef.current ? newestThreadMessageId : selectedThreadMessageId;
    scrollToLatestAfterSendRef.current = false;
    if (!targetId) return undefined;
    setHighlightedMessageId(targetId);
    const firstFrame = requestAnimationFrame(() => requestAnimationFrame(() => {
      threadMessageRefs.current.get(targetId)?.scrollIntoView?.({ block: selectedThreadMessageId ? "center" : "end", behavior: "auto" });
    }));
    clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedMessageId((current) => current === targetId ? "" : current), 2600);
    return () => cancelAnimationFrame(firstFrame);
  }, [detail, newestThreadMessageId, selectedThreadMessageId, thread]);

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  const connect = async (reconnectConnection = null) => {
    const actionKey = reconnectConnection ? `reconnect:${reconnectConnection.id}` : "connect";
    setOauthStarting(actionKey); setError("");
    try {
      const { data } = await api.post("/care/mail/oauth/google/start", null, { params: {
        connection_id: reconnectConnection?.id || undefined,
        return_path: mailOauthReturnPath(),
      } });
      if (!data?.authorization_url) throw new Error("oauth_authorization_url_missing");
      window.location.assign(data.authorization_url);
    } catch (requestError) {
      setError(apiError(requestError.response?.data?.detail) || t("mailReconnectStartError"));
      setOauthStarting("");
    }
  };
  const disconnect = async () => {
    await api.post(`/care/mail/connections/${connection.id}/disconnect`); setSelectedId(""); setSelectedThreadMessageId(""); setDetail(null); await loadStatus();
  };
  const link = async () => {
    if (!detail || !linkPlayer) return;
    setError("");
    try {
      await api.post(`/care/mail/connections/${connection.id}/messages/${detail.id}/links`, { player_id: linkPlayer, service_id: linkService || null });
      setAssignmentAttention(false);
      await openMessage(detail.id, false); await loadMessages(currentPage);
    } catch (requestError) { setError(apiError(requestError.response?.data?.detail)); }
  };
  const toggleRead = async () => {
    if (!detail) return;
    const read = Boolean(detail.unread);
    await api.post(`/care/mail/messages/${detail.id}/read`, { read }, { params: { connection_id: connection.id } });
    if (read) manualUnreadIds.current.delete(detail.id);
    else manualUnreadIds.current.add(detail.id);
    setDetail({ ...detail, unread: !read });
    setMessages((rows) => rows.map((row) => row.id === detail.id ? { ...row, unread: !read } : row));
    const next = new URLSearchParams(searchParams);
    next.set("view", "email"); next.set("connection", connection.id); next.set("message", detail.id);
    if (read) next.delete("keep_unread");
    else next.set("keep_unread", "1");
    setSearchParams(next, { replace: true });
  };
  const moveMessageToTrash = async (messageId) => {
    if (!connection || !messageId || trashingMessageId) return;
    setTrashingMessageId(messageId); setError("");
    const removedSelectedMessage = selectedId === messageId || detail?.id === messageId;
    try {
      await api.post(`/care/mail/messages/${messageId}/trash`, null, { params: { connection_id: connection.id } });
      manualUnreadIds.current.delete(messageId);
      setMessages((rows) => rows.filter((row) => row.id !== messageId));
      if (removedSelectedMessage) {
        setSelectedId(""); setDetail(null); setComposer(null);
        const next = new URLSearchParams(searchParams);
        next.set("view", "email"); next.set("connection", connection.id);
        next.delete("message"); next.delete("keep_unread");
        setSearchParams(next, { replace: true });
      }
      await loadMessages(currentPage);
      if (removedSelectedMessage) setSelectedId("");
    } catch (_requestError) {
      setError(t("mailTrashError"));
    } finally { setTrashingMessageId(""); }
  };
  const restoreMessage = async (messageId) => {
    if (!connection || !messageId || restoringMessageId) return;
    setRestoringMessageId(messageId); setError("");
    const removedSelectedMessage = selectedId === messageId || detail?.id === messageId;
    try {
      await api.post(`/care/mail/messages/${messageId}/restore`, null, { params: { connection_id: connection.id } });
      setMessages((rows) => rows.filter((row) => row.id !== messageId));
      if (removedSelectedMessage) {
        setSelectedId(""); setDetail(null); setComposer(null);
        const next = new URLSearchParams(searchParams);
        next.set("view", "email"); next.set("connection", connection.id);
        next.delete("message"); next.delete("keep_unread");
        setSearchParams(next, { replace: true });
      }
      await loadMessages(currentPage);
      if (removedSelectedMessage) setSelectedId("");
    } catch (_requestError) {
      setError(t("mailRestoreError"));
    } finally { setRestoringMessageId(""); }
  };
  const sent = async (draft, data) => {
    setComposer(null);
    if (draft.context?.communication_type === "staff_document_forwarding") {
      try {
        await api.patch(`/admin/staff-documents/${draft.context.staff_document_id}/status`, {
          status: "forwarded", message_id: data?.message_id, thread_id: data?.thread_id || null,
        });
        const event = {
          type: "performance-care:staff-document-forwarded",
          staff_document_id: draft.context.staff_document_id,
        };
        try { window.opener?.postMessage(event, window.location.origin); } catch {}
        if (typeof BroadcastChannel !== "undefined") {
          const channel = new BroadcastChannel("performance-care-staff-events");
          channel.postMessage(event); channel.close();
        }
      } catch (_requestError) { setError(t("staffForwardStatusError")); }
    }
    if (draft.mode === "reply" || draft.mode === "reply_all") {
      scrollToLatestAfterSendRef.current = true;
      await openMessage(detail.id, false);
    }
    else {
      setFolder("sent");
      const next = new URLSearchParams(searchParams);
      next.set("view", "email"); next.set("connection", connection.id);
      next.delete("quote_service"); next.delete("quote_provider"); next.delete("lang");
      if (data?.message_id) next.set("message", data.message_id);
      setSearchParams(next, { replace: true });
      if (draft.context?.communication_type === "quote_request") {
        const event = { type: "performance-care:service-quote-request-sent", service_id: draft.context.service_id };
        try { window.opener?.postMessage(event, window.location.origin); } catch {}
        if (typeof BroadcastChannel !== "undefined") {
          const channel = new BroadcastChannel("performance-care-service-events");
          channel.postMessage(event); channel.close();
        }
      }
      await loadMessages();
    }
  };
  const replySubject = (source = detail) => {
    const subject = String(source?.subject || detail.subject || t("mailNoSubject"));
    return /^Re:\s*/i.test(subject) ? subject : `Re: ${subject}`;
  };
  const forwardSubject = (source = detail) => {
    const subject = String(source?.subject || detail.subject || t("mailNoSubject"));
    return /^Fwd:\s*/i.test(subject) ? subject : `Fwd: ${subject}`;
  };
  const openThreadComposer = (draft) => {
    setComposer(draft);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      composerSlotRef.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
    }));
  };
  const startReply = (mode) => {
    const source = selectedThreadMessage || latestExternal;
    const to = replyAddressForMessage(source, ownedMailboxEmails);
    if (!source || !to || isOwnMailMessage(source, ownedMailboxEmails)) return;
    const identity = senderIdentity(source.reply_to || source.sender || to);
    openThreadComposer({
      ...emptyComposer, mode, sourceId: source.id, to,
      cc: mode === "reply_all" ? replyAllCcForMessage(source, ownedMailboxEmails) : "",
      subject: replySubject(source),
      replyContext: { name: identity.name || identity.address || to, date: messageDate(source.date, lang) },
    });
  };
  const startForward = () => openThreadComposer({
    ...emptyComposer, mode: "forward", sourceId: selectedThreadMessage?.id || detail.id,
    subject: forwardSubject(selectedThreadMessage || detail),
  });
  const changeFolder = (nextFolder) => {
    setFolder(nextFolder); setDetail(null); setSelectedId(""); setSelectedThreadMessageId(""); setError("");
    const next = new URLSearchParams(searchParams); next.set("view", "email"); next.delete("message"); next.delete("focus_message"); next.delete("keep_unread"); setSearchParams(next, { replace: true });
  };

  const openAttachmentWorkspace = (sourceMessageId = "", attachmentId, documentType = "", scopeToMessage = false) => {
    threadScrollPositionRef.current = detailScrollRef.current?.scrollTop || 0;
    const scoped = scopeToMessage ? sourceMessageId : "";
    const available = scoped ? threadAttachments.filter((row) => row.source_message_id === scoped) : threadAttachments;
    setAttachmentScopeMessageId(scoped);
    setSelectedAttachmentKey(attachmentId === null ? "" : attachmentId
      ? `${sourceMessageId}:${attachmentId}`
      : available[0] ? `${available[0].source_message_id}:${available[0].id}` : "");
    setFocusedDocumentType(documentType);
    setDocumentError(""); setDocumentFieldErrors({});
    setAttachmentWorkspaceOpen(true);
  };
  const openSuggestedDocument = (part, documentType) => {
    const attachments = part.attachments || [];
    openAttachmentWorkspace(part.id, attachments.length === 1 ? attachments[0].id : null, documentType, attachments.length > 1);
  };
  const closeAttachmentWorkspace = () => {
    setAttachmentWorkspaceOpen(false); setAttachmentScopeMessageId("");
    setDocumentError(""); setDocumentFieldErrors({});
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (detailScrollRef.current) detailScrollRef.current.scrollTop = threadScrollPositionRef.current;
    }));
  };
  const storeAttachmentDocument = async (attachment, documentType, metadata) => {
    if (!attachment || documentBusy || documentImportingRef.current) return false;
    if (!detail?.link?.service_id) {
      setDocumentError(t("mailAssignServiceFirst")); setAssignmentAttention(true);
      requestAnimationFrame(() => {
        assignmentPanelRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
        serviceSelectRef.current?.focus?.();
      });
      return false;
    }
    documentImportingRef.current = true;
    setDocumentBusy(true); setDocumentError(""); setDocumentFieldErrors({});
    setAssignmentAttention(false);
    try {
      const { data } = await api.post(`/care/mail/connections/${connection.id}/messages/${detail.id}/attachment-document`, {
        source_message_id: attachment.source_message_id,
        attachment_id: attachment.id,
        document_type: documentType,
        ...metadata,
      });
      const applyImport = (part) => ({ ...part, attachments: (part.attachments || []).map((row) => (
        part.id === attachment.source_message_id && row.id === attachment.id
          ? { ...row, document_import: data.document_import } : row
      )) });
      setDetail((current) => {
        if (!current) return current;
        const quoteDocuments = documentType === "quote" && data.document
          ? [...(current.link?.service?.quote_documents || []).filter((row) => row.id !== data.document.id), data.document]
          : current.link?.service?.quote_documents;
        return {
          ...applyImport(current),
          link: current.link?.service ? {
            ...current.link,
            service: { ...current.link.service, quote_documents: quoteDocuments || [] },
          } : current.link,
          thread: (current.thread || []).map(applyImport),
        };
      });
      setFocusedDocumentType(documentType);
      const event = { type: "performance-care:service-document-imported", service_id: detail.link.service_id };
      try { window.opener?.postMessage(event, window.location.origin); } catch {}
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel("performance-care-service-events");
        channel.postMessage(event); channel.close();
      }
      return true;
    } catch (requestError) {
      const responseDetail = requestError.response?.data?.detail;
      const importFallback = documentType === "quote" ? t("mailOfferImportError")
        : documentType === "booking_confirmation" ? t("mailCommissionImportError")
          : t("mailDocumentImportError");
      if (responseDetail?.code === "mail_service_assignment_required") {
        setDocumentError(t("mailAssignServiceFirst")); setAssignmentAttention(true);
      } else if (responseDetail?.code === "mail_attachment_already_classified") {
        setDocumentError(t("mailAlreadyClassified"));
      } else {
        const fields = apiFieldErrors(responseDetail);
        const mappedFields = {
          ...(fields.invoice_number ? { invoice_number: t("mailInvoiceNumberRequired") } : {}),
          ...(fields.amount ? { amount: t("mailInvoiceAmountInvalid") } : {}),
          ...(fields.currency ? { currency: apiError(fields.currency, importFallback) } : {}),
          ...(fields.quoted_amount ? { quoted_amount: t("mailOfferAmountInvalid") } : {}),
          ...(fields.related_offer_document_id ? { related_offer_document_id: t("mailRelatedOfferInvalid") } : {}),
        };
        setDocumentFieldErrors(mappedFields);
        setDocumentError(Object.keys(mappedFields).length
          ? "" : apiError(responseDetail, importFallback));
      }
      return false;
    } finally { documentImportingRef.current = false; setDocumentBusy(false); }
  };

  if (state === "loading" && !status) return <div className="grid place-items-center py-24"><div className="pc-spinner" /></div>;
  if (!connection) return <section className="pc-mail-disconnected pc-card" data-testid="mail-disconnected">
    <span className="pc-mail-hero-icon"><Mail size={30} /></span><h2>{t("mailConnectTitle")}</h2><p>{quoteServiceId ? t("quoteRequestConnectMailbox") : t("mailConnectHint")}</p>
    <button type="button" className="pc-btn pc-btn-primary" onClick={() => connect()} disabled={!status?.configured || Boolean(oauthStarting)}>{t("mailConnectGoogle")}</button>
    {!status?.configured && <p className="text-xs">{t("mailManualConfig")}</p>}{error && <p role="alert" className="pc-mail-error">{error}</p>}
  </section>;

  return <section className="pc-mail-workspace" data-testid="care-email-workspace">
    <aside className="pc-mail-folders pc-card">
      <div className="pc-mail-connection" data-connection-status={connection.status}><span className="pc-mail-connection__icon"><Mail size={17} /></span><div><small>{t("mailConnectedMailbox")}</small><b>Performance Care</b>{status.connections.length > 1 ? <select className="pc-input" value={connection.id} onChange={(event) => setConnectionId(event.target.value)}>{status.connections.map((row) => <option value={row.id} key={row.id}>{row.mailbox_email}</option>)}</select> : <strong title={connection.mailbox_email}>{connection.mailbox_email}</strong>}<span><i />{connection.status === "connected" ? t("mailConnected") : connection.status === "reconnect_required" ? t("mailReconnectRequired") : connection.status === "temporary_error" ? t("mailTemporaryError") : connection.status === "disconnected" ? t("mailDisconnectedStatus") : t("mailConnectionError")}</span></div></div>
      {oauthOutcome && <p className="pc-mail-oauth-feedback" data-status={oauthOutcome === "connected" ? "success" : "error"} role={oauthOutcome === "connected" ? "status" : "alert"}>{oauthOutcome === "connected" ? t("mailReconnectSucceeded") : oauthOutcome === "cancelled" ? t("mailReconnectCancelled") : oauthOutcome === "account_mismatch" ? t("mailReconnectAccountMismatch") : t("mailReconnectFailed")}</p>}
      {connection.status === "reconnect_required" && <button className="pc-btn pc-btn-primary pc-mail-reconnect" type="button" disabled={Boolean(oauthStarting)} onClick={() => connect(connection)}><RefreshCw size={16} />{oauthStarting === `reconnect:${connection.id}` ? t("loading") : t("mailReconnectGoogle")}</button>}
      <button className="pc-btn pc-btn-primary pc-mail-new" type="button" onClick={() => setComposer({ ...emptyComposer })}><Plus size={16} />{t("mailNew")}</button>
      <button className="pc-mail-connect-another" type="button" disabled={Boolean(oauthStarting)} onClick={() => connect()}>{t("mailConnectAnother")}</button>
      <nav aria-label={t("mailFolders")}>{MAIL_FOLDERS.map(([key, Icon]) => <button type="button" key={key} onClick={() => changeFolder(key)} className="pc-mail-folder" aria-current={folder === key ? "page" : undefined}><Icon size={17} /><span>{t(`mailFolder_${key}`)}</span>{key === "unread" && unreadCount > 0 && <b>{unreadCount}</b>}</button>)}</nav>
      <button type="button" className="pc-mail-disconnect" onClick={disconnect}><Unlink size={14} />{t("mailDisconnect")}</button>
    </aside>
    <div className="pc-mail-list pc-card">
      <div className="pc-mail-list__title"><div><small>{t(`mailFolder_${folder}`)}</small><strong>{state === "error" && messages.length === 0 ? "–" : messages.length}</strong></div><button type="button" className="pc-mail-icon-button" aria-label={t("refresh")} onClick={() => loadMessages(currentPage)}><RefreshCw size={16} /></button></div>
      <form className="pc-mail-search" onSubmit={(event) => { event.preventDefault(); setAppliedQuery(query.trim()); }}><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("mailSearch")} />{query && <button type="button" aria-label={t("mailClearSearch")} onClick={() => { setQuery(""); setAppliedQuery(""); }}><X size={14} /></button>}</form>
      {error && <div className="pc-mail-list-error" role="alert"><span>{error}</span>{connection.status !== "reconnect_required" && <button type="button" onClick={() => loadMessages(currentPage)}>{t("mailTryAgain")}</button>}</div>}
      <div className="pc-mail-message-list" aria-busy={state === "loading"}>
        {state === "loading" && messages.length === 0 && <div className="pc-mail-skeleton" aria-label={t("loading")}>{[1,2,3,4,5].map((row) => <span key={row} />)}</div>}
        {messages.map((row) => <div key={row.id} className="pc-mail-row-wrap">
          <button type="button" onClick={() => openMessage(row.id)} className="pc-mail-row" data-unread={row.unread ? "true" : "false"} aria-current={selectedId === row.id ? "true" : undefined}>
            <span className="pc-mail-row-top"><strong>{row.sender || row.to || t("mailUnknownSender")}</strong><time>{messageDate(row.date, lang, true)}</time></span>
            <span className="pc-mail-row-subject">{row.subject || t("mailNoSubject")}</span><span className="pc-mail-row-preview">{row.preview ?? row.snippet}</span>
            <span className="pc-mail-row-indicators">{row.has_attachments && <Paperclip size={13} aria-label={t("mailHasAttachments")} />}{row.link?.player_id && <Link2 size={13} aria-label={t("mailHasAssignment")} />}</span>
          </button>
          {folder !== "trash" ? <button type="button" className="pc-mail-row-trash" data-testid={`mail-trash-${row.id}`} aria-label={`${t("mailMoveToTrash")}: ${row.subject || t("mailNoSubject")}`} title={t("mailMoveToTrash")} disabled={trashingMessageId === row.id} onClick={() => moveMessageToTrash(row.id)}><Trash2 size={15} /></button>
            : <button type="button" className="pc-mail-row-restore" data-testid={`mail-restore-${row.id}`} aria-label={`${t("mailRestore")}: ${row.subject || t("mailNoSubject")}`} title={t("mailRestore")} disabled={restoringMessageId === row.id} onClick={() => restoreMessage(row.id)}><RotateCcw size={15} /></button>}
        </div>)}
        {state === "ready" && messages.length === 0 && <div className="pc-mail-list-empty"><MailOpen size={25} /><p>{appliedQuery ? t("mailSearchEmpty") : t(EMPTY_FOLDER_KEYS[folder] || "mailNoMessages")}</p>{appliedQuery && <button type="button" onClick={() => { setQuery(""); setAppliedQuery(""); }}>{t("mailClearSearch")}</button>}</div>}
      </div>
      <footer className="pc-mail-pages"><button disabled={!pageStack.length} onClick={() => { const previous = [...pageStack]; const token = previous.pop() || null; setPageStack(previous); setCurrentPage(token); loadMessages(token); }}><ArrowLeft size={14} />{t("previous")}</button><button disabled={!nextPage} onClick={() => { setPageStack((rows) => [...rows, currentPage]); setCurrentPage(nextPage); loadMessages(nextPage); }}>{t("next")}<span>→</span></button></footer>
    </div>
    <article className="pc-mail-detail pc-card">
      {state !== "error" && !detail && !composer && <div className="pc-mail-empty"><span><MailOpen size={25} /></span><h3>{t("mailSelectTitle")}</h3><p>{t("mailSelectMessage")}</p></div>}
      {composer?.mode === "new" && <Composer connection={connection} draft={composer} onChange={setComposer} onClose={() => setComposer(null)} onSent={sent} />}
      {detail && composer?.mode !== "new" && <>
        <div className="pc-mail-detail__chrome">
          <header className="pc-mail-detail__header"><div><h2>{detail.subject || t("mailNoSubject")}</h2><p>{t("mailConversation")}{thread.length ? ` · ${thread.length}` : ""}</p></div><button type="button" className="pc-mail-icon-button" title={t("more")}><MoreHorizontal size={18} /></button></header>
          <aside ref={assignmentPanelRef} className="pc-mail-context" data-testid="mail-context-panel" data-required={assignmentAttention ? "true" : "false"}>
            <div className="pc-mail-context__title"><span><Link2 size={15} /></span><div><small>{t("mailContextLabel")}</small><strong>{detail.link ? t("mailLinkedWith") : t("mailAssignTitle")}</strong></div></div>
            {detail.link && <div className="pc-mail-context__linked" data-testid="mail-linked-context">
              <span><UserRound size={14} /><b>{detail.link.player_name || players.find((row) => row.player.id === detail.link.player_id)?.player.name || detail.link.player_id}</b></span>
              {detail.link.service_id && <span><Wrench size={14} /><b>{detail.link.service?.category_name || detail.link.service?.title || services.find((row) => row.id === detail.link.service_id)?.title || detail.link.service_id}</b></span>}
              <div><button type="button" onClick={() => nav(`/care/players/${encodeURIComponent(detail.link.player_id)}`)}>{t("toPlayer")}</button>{detail.link.service_id && <button type="button" onClick={() => nav(`/care?view=services&service=${encodeURIComponent(detail.link.service_id)}`)}>{t("toService")}</button>}</div>
            </div>}
            <div className="pc-mail-context__form" data-testid="mail-assignment-form"><select className="pc-input" value={linkPlayer} data-testid="mail-player-link" onChange={(event) => { setLinkPlayer(event.target.value); setLinkService(""); }}><option value="">{t("mailAssignPlayer")}</option>{players.map((row) => <option key={row.player.id} value={row.player.id}>{row.player.name}</option>)}</select><select ref={serviceSelectRef} className="pc-input" value={linkService} data-testid="mail-service-link" disabled={!linkPlayer} aria-invalid={assignmentAttention ? "true" : undefined} onChange={(event) => { setLinkService(event.target.value); setAssignmentAttention(false); }}><option value="">{t("mailAssignServiceOptional")}</option>{services.map((row) => <option key={row.id} value={row.id}>{serviceOptionLabel(row, players.find((item) => item.player.id === linkPlayer)?.player || row.player, lang)}</option>)}</select><button type="button" className="pc-btn pc-btn-primary" disabled={!linkPlayer} onClick={link}>{t(detail.link ? "mailSaveAssignment" : "mailAssign")}</button></div>
          </aside>
        </div>
        {attachmentWorkspaceOpen ? <AttachmentWorkspace
          connection={connection} rows={visibleWorkspaceAttachments}
          selectedKey={selectedAttachmentKey} onSelect={setSelectedAttachmentKey}
          onBack={closeAttachmentWorkspace} onImport={storeAttachmentDocument}
          focusedDocumentType={focusedDocumentType} setFocusedDocumentType={setFocusedDocumentType}
          linkedService={detail.link?.service} linkedProvider={detail.link?.provider}
          linkedProviderId={detail.link?.provider_id} busy={documentBusy} error={documentError}
          fieldErrors={documentFieldErrors} onFieldErrors={setDocumentFieldErrors}
        /> : <div className="pc-mail-detail__scroll" data-testid="mail-detail-scroll" ref={detailScrollRef}>
          <div className="pc-mail-thread" data-testid="mail-thread" role="listbox" aria-label={t("mailConversation")}>{thread.map((part, index) => {
            const sender = senderIdentity(part.sender || t("mailUnknownSender"));
            const ownMessage = isOwnMailMessage(part, ownedMailboxEmails);
            const isSelected = selectedThreadMessageId === part.id;
            const attachments = part.attachments || [];
            return <section
              key={part.id}
              ref={(node) => { if (node) threadMessageRefs.current.set(part.id, node); else threadMessageRefs.current.delete(part.id); }}
              className="pc-mail-thread-message"
              data-latest={index === thread.length - 1 ? "true" : "false"}
              data-own={ownMessage ? "true" : "false"}
              data-highlighted={highlightedMessageId === part.id ? "true" : "false"}
              data-selected={isSelected ? "true" : "false"}
              data-message-id={part.id}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              onClick={() => setSelectedThreadMessageId(part.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault(); setSelectedThreadMessageId(part.id);
                }
              }}
            >
              <div className="pc-mail-thread-meta"><span className="pc-mail-sender-avatar">{String(sender.name || "?").trim().charAt(0).toUpperCase()}</span><div><strong>{sender.name}</strong>{sender.address && <span className="pc-mail-sender-address">{sender.address}</span>}<details><summary>{t("mailRecipientDetails")}</summary><p>{t("mailTo")}: {part.to || "–"}{part.cc ? <><br />{t("mailCcShort")}: {part.cc}</> : null}</p></details></div><time>{messageDate(part.date, lang)}</time>{isSelected && <span className="pc-mail-thread-selected-label">{t("mailSelected")}</span>}</div>
              <div className="pc-mail-message-body"><MessageBody part={part} /></div>
              <AttachmentList connectionId={connection.id} part={part} mailboxEmails={(status.connections || []).map((row) => row.mailbox_email)} onOpen={openAttachmentWorkspace} onSuggest={openSuggestedDocument} />
            </section>;
          })}</div>
          {composer && <div className="pc-mail-composer-slot" ref={composerSlotRef}><Composer connection={connection} draft={composer} onChange={setComposer} onClose={() => setComposer(null)} onSent={sent} /></div>}
        </div>}
        {!attachmentWorkspaceOpen && <div className="pc-mail-actions pc-mail-actions--thread" data-testid="mail-thread-actions">
          <button disabled={!selectedThreadMessage || !selectedReplyAddress || isOwnMailMessage(selectedThreadMessage, ownedMailboxEmails)} onClick={() => startReply("reply")}><CornerUpLeft size={15} />{t("mailReply")}</button>
          <button disabled={!selectedThreadMessage || !selectedReplyAddress || isOwnMailMessage(selectedThreadMessage, ownedMailboxEmails)} onClick={() => startReply("reply_all")}><ReplyAll size={15} />{t("mailReplyAll")}</button>
          <button onClick={startForward}><Forward size={15} />{t("mailForward")}</button>
          <button onClick={toggleRead}>{detail.unread ? <MailOpen size={15} /> : <Mail size={15} />}{t(detail.unread ? "mailMarkRead" : "mailMarkUnread")}</button>
          {threadAttachments.length > 0 && <button onClick={() => openAttachmentWorkspace()} data-testid="mail-all-attachments"><Paperclip size={15} />{t("mailAllAttachments")} · {threadAttachments.length}</button>}
          {folder === "trash" && <button className="pc-mail-action-restore" disabled={restoringMessageId === detail.id} onClick={() => restoreMessage(detail.id)}><RotateCcw size={15} />{t("mailRestore")}</button>}
          {selectedThreadMessage && isOwnMailMessage(selectedThreadMessage, ownedMailboxEmails) && <p className="pc-mail-reply-hint" role="status">{t("mailOwnMessageReplyHint")}</p>}
          {selectedThreadMessage && !isOwnMailMessage(selectedThreadMessage, ownedMailboxEmails) && !selectedReplyAddress && <p className="pc-mail-reply-hint" role="status">{t("mailNoExternalReply")}</p>}
          {!latestExternal && <p className="pc-mail-reply-hint" role="status">{t("mailNoExternalReply")}</p>}
        </div>}
      </>}
    </article>
  </section>;
}
